<script>
import TruckStatus from "@/components/statuses/TruckStatus.vue";
import UserStatus from "@/components/statuses/UserStatus.vue";
import OpenChatButton from "@/components/buttons/OpenChatButton.vue";
import { mapState } from "pinia";
import { useAppStore } from "@/store/app.store.ts";
import GoogleMapsMixin from "@/mixins/google-maps.mixin.ts";
import CurrentLocationDot from "@/components/statuses/CurrentLocationDot.vue";
import { DAY_IN_SECONDS, HOUR_IN_SECONDS, TWENTY_MINUTES_IN_SECONDS } from "@/constants/time.ts";
import { useDateTime } from "@/composables/index.ts";

const { dateTZ } = useDateTime();

export default {
  name: "UserTruck",
  components: { CurrentLocationDot, OpenChatButton, UserStatus, TruckStatus },
  mixins: [GoogleMapsMixin],
  props: {
    truck: {
      type: Object,
      required: true,
    },
    user: Object,
    excludeUser: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapState(useAppStore, ["dispatcher"]),
    showFirstDriver() {
      return this.truck.driver?.id && (!this.excludeUser || this.truck.driver?.id !== this.user?.id);
    },
    showSecondDriver() {
      return this.truck.driver2?.id && (!this.excludeUser || this.truck.driver2?.id !== this.user?.id);
    },
    dotSettings() {
      return [
        {
          seconds: DAY_IN_SECONDS,
          color: "grey",
        },
        {
          seconds: HOUR_IN_SECONDS,
          color: "orange",
        },
        {
          seconds: TWENTY_MINUTES_IN_SECONDS,
          color: "yellow",
        },
      ];
    },
    truckAvailableData() {
      return this.dispatcher ? dateTZ(this.truck.availableDate, this.dispatcher.timezone) : "";
    },
  },
};
</script>

<template>
  <div class="truck-block">
    <div class="d-flex justify-space-between align-center mb-2">
      <div class="mr-2 overflow-hidden">
        <router-link
          :to="{ name: 'truck-view', params: { id: truck.id } }"
          class="text-decoration-none d-flex align-center"
          target="_blank"
        >
          <div class="d-flex align-center justify-center mr-1 icon-block--medium">
            <v-icon class="text-primary" size="16">mdi-truck-outline</v-icon>
          </div>
          <span class="text-primary font-weight-medium">{{ $t("trucks.truck") }} </span>
          <span
            class="ml-1 text-primary font-weight-medium text-uppercase overflow-hidden white-space-nowrap text-overflow-ellipsis"
            data-qa="additional-panel-truck-number"
          >
            {{ truck.number }}
          </span>
        </router-link>
      </div>
      <truck-status :truck="truck" menu-location="bottom start" />
    </div>

    <div
      v-if="truck?.availableLocation"
      :class="{
        'mb-2': truck?.currentLocation,
      }"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-1" color="dark-grey" size="14">mdi-calendar-month-outline</v-icon>
        <div class="text-grey-darken-2 font-size-12">Available location:</div>
      </div>

      <div class="d-flex align-center">
        <span class="text-grey-darken-2 font-size-12 font-weight-500" data-qa="truck-available-location">
          {{ truck.availableLocation.fullAddress || "Location unknown" }}
        </span>

        <a
          v-if="truck.availableLocation.coordinates"
          :href="getPlaceUrl(truck.availableLocation.coordinates)"
          target="_blank"
        >
          <v-icon class="ml-1" color="success" size="14">mdi-map-marker-radius-outline</v-icon>
        </a>
      </div>

      <span class="font-size-12 text-grey-darken-2" data-qa="truck-available-location-time">{{
        truckAvailableData
      }}</span>
    </div>

    <v-divider v-if="truck?.currentLocation" class="mb-2"></v-divider>

    <div v-if="truck?.currentLocation">
      <div class="d-flex align-center">
        <v-icon color="green-darken-2 mr-1" size="14">mdi-map-marker</v-icon>
        <div class="font-size-12 font-weight-500 text-green-darken-2">Current location:</div>
      </div>

      <div class="d-flex align-center">
        <span class="text-grey-darken-2 font-size-12 font-weight-500" data-qa="truck-current-location">
          {{ truck.currentLocation.fullAddress || "Location unknown" }}
        </span>

        <a
          v-if="truck.currentLocation.coordinates"
          :href="getPlaceUrl(truck.currentLocation.coordinates)"
          target="_blank"
        >
          <v-icon class="ml-1" color="success" size="14">mdi-map-marker-radius-outline</v-icon>
        </a>
      </div>

      <current-location-dot
        v-if="truck.currentLocationTime"
        :dot-settings="dotSettings"
        :time="truck.currentLocationTime"
        dot-class="font-size-12 text-grey-darken-2 ml-10px"
      />
    </div>

    <div v-if="showFirstDriver" class="d-flex align-center justify-space-between truck-driver__info">
      <div class="d-flex align-center overflow-hidden mr-3">
        <div class="d-flex align-center overflow-hidden mr-2">
          <div
            class="bg-white text-indigo-lighten-2 font-weight-600 status-item text-uppercase rounded-circle mr-2 d-flex align-center justify-center font-size-11"
          >
            DR
          </div>
          <v-tooltip location="top start">
            <template #activator="{ props }">
              <router-link
                :to="{ name: 'user-view', params: { id: truck.driver.id } }"
                class="text-primary text-caption font-weight-medium driver-switch-name text-decoration-none"
                target="_blank"
                v-bind="props"
              >
                {{ truck.driver.name }}
              </router-link>
            </template>
            First driver
          </v-tooltip>
        </div>
        <user-status :status="truck.driver.driverStatus" />
      </div>
      <open-chat-button :user="truck.driver"></open-chat-button>
    </div>
    <div v-if="showSecondDriver" class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <div class="d-flex align-center overflow-hidden mr-2">
          <div class="d-flex align-center justify-center icon-block--medium mr-1">
            <v-icon class="text-grey-darken-2" size="16">mdi-account-switch-outline</v-icon>
          </div>
          <v-tooltip location="top start">
            <template #activator="{ props }">
              <router-link
                :to="{ name: 'user-view', params: { id: truck.driver2.id } }"
                class="text-grey-darken-1 caption driver-switch-name text-decoration-none"
                target="_blank"
                v-bind="props"
              >
                {{ truck.driver2.name }}
              </router-link>
            </template>
            Second driver
          </v-tooltip>
        </div>
        <user-status :status="truck.driver2.driverStatus" />
      </div>
      <open-chat-button :user="truck.driver2"></open-chat-button>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
